// Market-stat approval queue (VRG-463) react-query hooks.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { askClaimsApi } from 'services'
import type { IAskClaim } from 'services/askClaimsApi'

const STATS_KEY = ['internal', 'ask-claims']
const ITEMS_KEY = ['internal', 'market-items']

export const useProposedClaimsQuery = () =>
  useQuery<IAskClaim[], Error>({
    queryKey: [...STATS_KEY, 'proposed'],
    queryFn: () => askClaimsApi.proposed(),
    retry: false,
    refetchOnWindowFocus: false,
  })

export const useApprovedClaimsQuery = () =>
  useQuery<IAskClaim[], Error>({
    queryKey: [...STATS_KEY, 'approved'],
    queryFn: () => askClaimsApi.approved(),
    retry: false,
    refetchOnWindowFocus: false,
  })

export const useRejectedClaimsQuery = () =>
  useQuery<IAskClaim[], Error>({
    queryKey: [...STATS_KEY, 'rejected'],
    queryFn: () => askClaimsApi.rejected(),
    retry: false,
    refetchOnWindowFocus: false,
  })

/**
 * `retry: false, networkMode: 'always'` throughout — required, not stylistic. The
 * global mutation default retries a 4xx exactly like a 5xx (VRG-510), which is what
 * left VRG-461's add-by-URL modal spinning forever on a rejected URL.
 *
 * It matters more here than anywhere else in this epic: extraction is a BILLED model
 * call, so a silent retry is a silent double charge.
 */
export const useExtractClaims = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (itemId: string) => askClaimsApi.extract(itemId),
    retry: false,
    networkMode: 'always',
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STATS_KEY })
      // Extraction also writes item-level tags (categories/brands), so the corpus
      // list is stale too.
      queryClient.invalidateQueries({ queryKey: ITEMS_KEY })
    },
  })
}

export const useApproveClaim = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => askClaimsApi.approve(id),
    retry: false,
    networkMode: 'always',
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STATS_KEY })
    },
  })
}

export const useRejectClaim = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      askClaimsApi.reject(id, reason),
    retry: false,
    networkMode: 'always',
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STATS_KEY })
    },
  })
}

/**
 * The Stats-tab bulk bar's "Approve" — one transaction on the API, one request here.
 * NOT a client-side loop over `useApproveClaim`: that would be N HTTP requests
 * for one click, exactly what working-conventions warns against for this class of
 * action.
 */
export const useBulkApproveClaims = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (ids: string[]) => askClaimsApi.bulkApprove(ids),
    retry: false,
    networkMode: 'always',
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STATS_KEY })
    },
  })
}

export const useBulkRejectClaims = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ids, reason }: { ids: string[]; reason: string }) =>
      askClaimsApi.bulkReject(ids, reason),
    retry: false,
    networkMode: 'always',
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STATS_KEY })
    },
  })
}
